import React, { FC, ReactElement, useState } from 'react';
import { MessageDescriptor } from 'react-intl';
import { useQueryClient } from 'react-query';

import { CURRENCIES } from '@savgroup-front-common/constants';
import {
  Carriers,
  TRANSPORT_METHOD_TYPE,
  TransportMethodType,
  TransportPlan,
} from '@savgroup-front-common/core/api/Claim/getClaimGroupTransportPlanQuery';
import { getTransportPlansWithoutRelayPointApi } from '@savgroup-front-common/core/api/Claim/helpers/getTransportPlansWithoutRelayPointApi';
import { hasRelayPointApi } from '@savgroup-front-common/core/api/Claim/helpers/hasRelayPointApi';
import {
  homeMethods,
  immediateDropAtStoreMethods,
  pickupMethods,
} from '@savgroup-front-common/core/api/Claim/hooks/useGetClaimGroupTransportPlanQuery';
import { ChooseItemCard as ChooseHandlingCard } from '@savgroup-front-common/core/atoms/ChooseItemCard';
import SwipeableDrawer from '@savgroup-front-common/core/atoms/SwipeableDrawer/SwipeableDrawer';
import { useCurrentUser } from '@savgroup-front-common/core/domains/login/useLoginStore';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { getHandlingCardLabel } from '@savgroup-front-common/core/formatters/getHandlingCardLabel/getHandlingCardLabel';
import {
  AddressInfoDto,
  HandlingSummary,
  MessageType,
  PickupPoint,
  StrictAutocompleteOption,
} from '@savgroup-front-common/types';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import {
  HandlingValues,
  PickupPointOption,
  SetTransportPlanWithoutRelayPointApiArgs,
} from '../../../IrshPages.types';
import DisplayHomeAddress from '../../DisplayHomeAddress/DisplayHomeAddress';
import { DisplayPickupPointAddress } from '../../DisplayPickupPointAddress/DisplayPickupPointAddress';

import { AddressManagement } from './AddressManagement/AddressManagement';
import messages from './messages';
import { SelectPickupPointDrawerContent } from './SelectPickupPointDrawerContent/SelectPickupPointDrawerContent';
import { GET_PICKUP_POINT_BY_GEO_CORD_QUERY } from './SelectPickupPointDrawerContent/SelectPickupPointDrawerContent.hooks';

interface ChooseItemCardProps {
  handleOnChange?: (...value: any) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  title?: string | number | boolean | MessageType | MessageDescriptor;
  highlighting?: string | number | boolean | MessageType | MessageDescriptor;
  description?: string | number | boolean | MessageType | MessageDescriptor;
  price?: number;
  icon?: ICONS_TYPE;
  logo?: ReactElement;
  actionButton?: ReactElement;
  hasDisplayFreeAmount?: boolean;
  dataTestId?: string;
  isSelected?: boolean;

  onSetPickupPoint: ({
    selectedPickupPoint,
    selectedMethod,
  }: {
    selectedPickupPoint?: StrictAutocompleteOption<{
      pickupPoint: PickupPoint;
      transportPlan: TransportPlan;
    }>;
    selectedMethod: StrictAutocompleteOption<TransportPlan[]>;
  }) => void;
  onSetAddress: ({
    address,
    selectedMethod,
  }: {
    address: AddressInfoDto;
    selectedMethod: StrictAutocompleteOption<TransportPlan[]>;
  }) => void;
  onSetTransportPlanWithoutRelayPointApi?: (
    args: SetTransportPlanWithoutRelayPointApiArgs,
  ) => void;
  storeAddress?: AddressInfoDto;
  availableMethod: StrictAutocompleteOption<TransportPlan[]>;
  handlingValues: HandlingValues;
  handlingSummary: HandlingSummary;
  pickupPointSuggested?: PickupPointOption;
}

export const ChooseHandlingMethod: FC<
  React.PropsWithChildren<ChooseItemCardProps>
> = ({
  icon = undefined,
  dataTestId,
  isSelected,
  onSetAddress,
  onSetPickupPoint,
  onSetTransportPlanWithoutRelayPointApi,
  availableMethod,
  handlingValues,
  handlingSummary,
  pickupPointSuggested,
}) => {
  const queryClient = useQueryClient();
  const { address, selectedPickupPoint, selectedTransportPlan } =
    handlingValues;
  const { ownerAddress, isAnonymous } = useCurrentUser();
  const hasHome = handlingSummary?.hasHome;
  const canDeduceTransportFromRefund =
    availableMethod?.data?.at(0)?.isDeductible;

  const isHomeMethod = homeMethods.includes(
    availableMethod.value as TransportMethodType,
  );

  const isPickupMethod = pickupMethods
    .concat(immediateDropAtStoreMethods)
    .includes(availableMethod.value as TransportMethodType);

  const isLabelProvidedByCustomerMethod =
    availableMethod.value === TRANSPORT_METHOD_TYPE.LabelProvidedByCustomer;

  const isDropAtStoreMethod =
    availableMethod.value === TRANSPORT_METHOD_TYPE.DropAtStoreDeposit;
  const isPickUpAtStoreMethod =
    availableMethod.value === TRANSPORT_METHOD_TYPE.PickUpStoreDelivery;

  const isDropAtExternalCarrier =
    availableMethod?.data?.at(0)?.carrier === Carriers.External;

  const canSelectTransportPlanWithoutRelayPointApi =
    availableMethod.value === TRANSPORT_METHOD_TYPE.RelayPointDeposit &&
    !!onSetTransportPlanWithoutRelayPointApi;

  const handleSelectTransportPlanWithoutRelayPointApi = React.useCallback(
    (transportPlan: TransportPlan) => {
      onSetTransportPlanWithoutRelayPointApi?.({
        transportPlan,
        selectedMethod: availableMethod,
      });
    },
    [onSetTransportPlanWithoutRelayPointApi, availableMethod],
  );

  const transportPlansWithoutRelayPointApi =
    getTransportPlansWithoutRelayPointApi(availableMethod.data);
  const [transportPlanToSelectDirectly] = transportPlansWithoutRelayPointApi;
  const shouldSelectTransportPlanDirectly =
    canSelectTransportPlanWithoutRelayPointApi &&
    !availableMethod.data.some(hasRelayPointApi) &&
    transportPlansWithoutRelayPointApi.length === 1;

  const [isSelectPickupPointDrawerOpen, setIsSelectPickupPointDrawerOpen] =
    useState(false);
  const [isAddressManagementDrawerOpen, setIsAddressManagementDrawerOpen] =
    useState(false);

  const handleCardClick = React.useCallback(() => {
    if (isHomeMethod) {
      setIsAddressManagementDrawerOpen(true);

      return;
    }

    if (isPickupMethod) {
      if (shouldSelectTransportPlanDirectly) {
        handleSelectTransportPlanWithoutRelayPointApi(
          transportPlanToSelectDirectly,
        );

        return;
      }

      if (
        pickupPointSuggested &&
        (isDropAtStoreMethod || isPickUpAtStoreMethod)
      ) {
        onSetPickupPoint({
          selectedMethod: availableMethod,
          selectedPickupPoint: pickupPointSuggested,
        });
      } else {
        queryClient.invalidateQueries(GET_PICKUP_POINT_BY_GEO_CORD_QUERY);
        setIsSelectPickupPointDrawerOpen(true);
      }

      return;
    }

    if (isLabelProvidedByCustomerMethod) {
      const actorAddress = handlingSummary.actorInfos?.address;

      if (!actorAddress) {
        return;
      }

      onSetAddress({
        address: actorAddress,
        selectedMethod: availableMethod,
      });

      return;
    }

    return;
  }, [
    isHomeMethod,
    isPickupMethod,
    shouldSelectTransportPlanDirectly,
    handleSelectTransportPlanWithoutRelayPointApi,
    transportPlanToSelectDirectly,
    pickupPointSuggested,
    isDropAtStoreMethod,
    isPickUpAtStoreMethod,
    onSetPickupPoint,
    availableMethod,
    queryClient,
    isLabelProvidedByCustomerMethod,
    handlingSummary.actorInfos?.address,
    onSetAddress,
  ]);

  const handleModifyCardClick = React.useCallback(() => {
    if (isHomeMethod) {
      setIsAddressManagementDrawerOpen(true);

      return;
    }

    if (isPickupMethod) {
      queryClient.invalidateQueries([GET_PICKUP_POINT_BY_GEO_CORD_QUERY]);
      setIsSelectPickupPointDrawerOpen(true);
    }

    return;
  }, [isHomeMethod, isPickupMethod, queryClient]);

  const { label, highlighting, description } = getHandlingCardLabel({
    transportMethodType: hasHome
      ? TRANSPORT_METHOD_TYPE.HomeIntervention
      : (availableMethod.value as TransportMethodType),
  });

  const transportPlans = availableMethod.data;

  const transportPlanWithMinPrice = transportPlans.reduce(
    (minPlan, currentPlan) => {
      if ((currentPlan.priceWithTax ?? 0) < (minPlan.priceWithTax ?? 0)) {
        return currentPlan;
      }

      return minPlan;
    },
    transportPlans[0],
  );
  const price = transportPlanWithMinPrice?.priceWithTax;

  const isAllTransportPlanHasSamePrice = transportPlans.every(
    (plan) => plan.priceWithTax === transportPlans[0].priceWithTax,
  );

  const pickupPointStoreSubLine = pickupPointSuggested?.data.pickupPoint ? (
    <DisplayPickupPointAddress
      onModifyClick={handleModifyCardClick}
      pickupPointSuggested={pickupPointSuggested}
    />
  ) : undefined;

  const pickupPointSubLine = selectedPickupPoint?.data?.pickupPoint ? (
    <DisplayPickupPointAddress
      pickupPointOption={selectedPickupPoint}
      onModifyClick={handleModifyCardClick}
    />
  ) : undefined;

  const relayPointCarrierSubLine = selectedTransportPlan ? (
    <SafeFormattedMessageWithoutSpread
      message={messages.relayPointDepositWithCarrier}
      values={{ carrierName: selectedTransportPlan.carrier }}
    />
  ) : undefined;

  const actorAddress = handlingSummary?.actorInfos?.address;
  const labelProvidedByCustomerSubLine = actorAddress ? (
    <DisplayHomeAddress address={actorAddress} />
  ) : undefined;

  const homeSubLine =
    !selectedPickupPoint && address ? (
      <DisplayHomeAddress address={address} onModifyClick={handleCardClick} />
    ) : undefined;

  const subLine = (
    availableMethod: StrictAutocompleteOption<TransportPlan[]>,
  ) => {
    if (isSelected) {
      switch (availableMethod.value) {
        case TRANSPORT_METHOD_TYPE.HomeIntervention:
          return homeSubLine;
        case TRANSPORT_METHOD_TYPE.RelayPointDeposit:
          return pickupPointSubLine ?? relayPointCarrierSubLine;
        case TRANSPORT_METHOD_TYPE.DropAtStoreDeposit:
        case TRANSPORT_METHOD_TYPE.PickUpStoreDelivery:
          return pickupPointSubLine;
        case TRANSPORT_METHOD_TYPE.LabelProvidedByCustomer:
          return labelProvidedByCustomerSubLine;
        default:
          return pickupPointSubLine;
      }
    }
    if (isLabelProvidedByCustomerMethod) {
      return labelProvidedByCustomerSubLine;
    }
    if (
      availableMethod.value === TRANSPORT_METHOD_TYPE.DropAtStoreDeposit ||
      availableMethod.value === TRANSPORT_METHOD_TYPE.PickUpStoreDelivery
    ) {
      return pickupPointStoreSubLine;
    }

    return undefined;
  };

  return (
    <div>
      <ChooseHandlingCard
        dataTestId={dataTestId}
        icon={icon}
        isActive={isSelected}
        subLine={subLine(availableMethod)}
        onClick={handleCardClick}
        label={label}
        highlighting={highlighting}
        description={description}
        price={{
          amount: price,
          currency: CURRENCIES.EUR,
        }}
        displayPriceAs={isAllTransportPlanHasSamePrice ? 'amount' : 'startFrom'}
        canDeduceTransportFromRefund={canDeduceTransportFromRefund}
      />
      <SwipeableDrawer
        isOpen={isSelectPickupPointDrawerOpen}
        onClose={() => setIsSelectPickupPointDrawerOpen(false)}
        hasBackdrop
        hasPadding={false}
        hasBackdropScroll={false}
        hasCloseButton={false}
      >
        <SelectPickupPointDrawerContent
          availableMethod={availableMethod}
          onSetPickupPoint={({
            pickupPoint,
          }: {
            pickupPoint: PickupPointOption;
          }) =>
            onSetPickupPoint({
              selectedMethod: availableMethod,
              selectedPickupPoint: pickupPoint,
            })
          }
          onClose={() => setIsSelectPickupPointDrawerOpen(false)}
          defaultSearchAddress={
            !isAnonymous && (address ?? ownerAddress)
              ? {
                  address: (address ?? ownerAddress)?.address,
                  countryCode: (address ?? ownerAddress)?.countryCode,
                  postalCode: (address ?? ownerAddress)?.postalCode,
                  city: (address ?? ownerAddress)?.city,
                }
              : undefined
          }
          onSelectTransportPlanWithoutRelayPointApi={
            canSelectTransportPlanWithoutRelayPointApi
              ? handleSelectTransportPlanWithoutRelayPointApi
              : undefined
          }
          handlingValues={handlingValues}
          isImmediateDropAtStoreMethod={isDropAtExternalCarrier}
        />
      </SwipeableDrawer>
      <SwipeableDrawer
        isOpen={isAddressManagementDrawerOpen}
        onClose={() => setIsAddressManagementDrawerOpen(false)}
        hasBackdrop
        hasBackdropScroll={false}
      >
        <AddressManagement
          defaultAddress={isAnonymous ? undefined : (address ?? ownerAddress)}
          onAddressSubmit={({ address }: { address: AddressInfoDto }) => {
            onSetAddress({
              address,
              selectedMethod: availableMethod,
            });

            setIsAddressManagementDrawerOpen(false);
          }}
        />
      </SwipeableDrawer>
    </div>
  );
};
